/**
 * Pure mutation functions on ProjectState drafts.
 *
 * Isomorphic: runs on client (Zustand + Mutative) and server (bare Mutative).
 * Every function receives a mutable draft and modifies it in place.
 * No side effects, no framework deps, no async.
 */

import type {
  ProjectState,
  ClipState,
  ClipDialog,
  ClipTransition,
  ClipEffects,
  AudioTrack,
  TextOverlay,
  Character,
  ProjectSettings,
} from './projectState'

const now = () => Date.now()

// ── Clip mutations ────────────────────────────────────────

export function trimClip(
  state: ProjectState,
  clipId: string,
  trimStart: number,
  trimEnd: number,
) {
  const clip = state.clips[clipId]
  if (!clip) return
  clip.trimStart = trimStart
  clip.trimEnd = trimEnd
  clip.updatedAt = now()
}

export function deleteClip(state: ProjectState, clipId: string) {
  const clip = state.clips[clipId]
  if (!clip) return
  clip.deletedAt = now()
  clip.updatedAt = now()
  state.clipOrder = state.clipOrder.filter((id) => id !== clipId)
  // Reindex remaining
  state.clipOrder.forEach((id, i) => {
    if (state.clips[id]) state.clips[id].orderIndex = i
  })
}

export function restoreClip(state: ProjectState, clipId: string) {
  const clip = state.clips[clipId]
  if (!clip || !clip.deletedAt) return // guard: no-op if already active
  clip.deletedAt = undefined
  clip.updatedAt = now()
  // Remove if somehow already in order, then insert at target position
  state.clipOrder = state.clipOrder.filter((id) => id !== clipId)
  const targetIdx = clip.orderIndex
  state.clipOrder.splice(
    Math.min(targetIdx, state.clipOrder.length),
    0,
    clipId,
  )
  // Reindex
  state.clipOrder.forEach((id, i) => {
    if (state.clips[id]) state.clips[id].orderIndex = i
  })
}

export function createClip(
  state: ProjectState,
  clip: ClipState,
) {
  const idx = clip.orderIndex
  // Shift downstream clips
  state.clipOrder.forEach((id) => {
    const c = state.clips[id]
    if (c && c.orderIndex >= idx) c.orderIndex++
  })
  state.clips[clip._id] = clip
  state.clipOrder.splice(idx, 0, clip._id)
}

export function reorderClips(state: ProjectState, newOrder: string[]) {
  state.clipOrder = newOrder
  newOrder.forEach((id, i) => {
    if (state.clips[id]) {
      state.clips[id].orderIndex = i
      state.clips[id].updatedAt = now()
    }
  })
}

/** General-purpose clip field update (patches any ClipState fields). */
export function updateClip(
  state: ProjectState,
  clipId: string,
  updates: Partial<Omit<ClipState, '_id' | 'workflowId' | 'createdAt'>>,
) {
  const clip = state.clips[clipId]
  if (!clip) return
  Object.assign(clip, updates)
  clip.updatedAt = now()
}

export function updateClipPrompt(
  state: ProjectState,
  clipId: string,
  videoPrompt: string,
) {
  const clip = state.clips[clipId]
  if (!clip) return
  clip.videoPrompt = videoPrompt
  clip.updatedAt = now()
}

export function updateClipTitle(
  state: ProjectState,
  clipId: string,
  title: string,
) {
  const clip = state.clips[clipId]
  if (!clip) return
  clip.title = title
  clip.updatedAt = now()
}

export function updateClipSpeed(
  state: ProjectState,
  clipId: string,
  speed: number,
) {
  const clip = state.clips[clipId]
  if (!clip) return
  clip.speed = speed
  clip.updatedAt = now()
}

export function updateClipTransition(
  state: ProjectState,
  clipId: string,
  transition: ClipTransition | undefined,
) {
  const clip = state.clips[clipId]
  if (!clip) return
  clip.transition = transition
  clip.updatedAt = now()
}

export function updateClipEffects(
  state: ProjectState,
  clipId: string,
  effects: Partial<ClipEffects>,
) {
  const clip = state.clips[clipId]
  if (!clip) return
  clip.effects = { ...clip.effects, ...effects } as ClipEffects
  clip.updatedAt = now()
}

export function updateClipDialog(
  state: ProjectState,
  clipId: string,
  dialog: ClipDialog[],
) {
  const clip = state.clips[clipId]
  if (!clip) return
  clip.dialog = dialog
  clip.updatedAt = now()
}

export function updateClipSceneLabel(
  state: ProjectState,
  clipId: string,
  sceneLabel: string | undefined,
) {
  const clip = state.clips[clipId]
  if (!clip) return
  clip.sceneLabel = sceneLabel
  clip.updatedAt = now()
}

export function updateClipAnimationKeyframes(
  state: ProjectState,
  clipId: string,
  animationKeyframes: ClipEffects['animationKeyframes'] | undefined,
) {
  const clip = state.clips[clipId]
  if (!clip) return
  if (!clip.effects) clip.effects = {} as ClipEffects
  clip.effects.animationKeyframes = animationKeyframes
  clip.updatedAt = now()
}

export function batchUpdateClipEffects(
  state: ProjectState,
  updates: Array<{ clipId: string; effects: Partial<ClipEffects> }>,
) {
  for (const { clipId, effects } of updates) {
    updateClipEffects(state, clipId, effects)
  }
}

// ── Workflow-level mutations ──────────────────────────────

export function updateNarrative(state: ProjectState, narrative: string) {
  state.workflow.narrative = narrative
}

export function updateTitle(state: ProjectState, title: string) {
  state.workflow.title = title
}

export function updateCharacters(state: ProjectState, characters: Character[]) {
  state.workflow.characters = characters
}

export function updateCharacter(
  state: ProjectState,
  characterId: string,
  updates: Partial<Omit<Character, 'id'>>,
) {
  const idx = state.workflow.characters.findIndex((c) => c.id === characterId)
  if (idx === -1) return
  Object.assign(state.workflow.characters[idx], updates)
}

export function updateGlobalContext(
  state: ProjectState,
  globalContext: string,
) {
  state.workflow.globalContext = globalContext
}

export function updateSettings(
  state: ProjectState,
  settings: Partial<ProjectSettings>,
) {
  Object.assign(state.workflow.settings, settings)
}

export function updateTimelineMode(
  state: ProjectState,
  mode: 'ripple' | 'preserve_positions',
) {
  state.workflow.timelineMode = mode
}

export function updateExportDuration(
  state: ProjectState,
  exportDuration: number | undefined,
) {
  state.workflow.exportDuration = exportDuration
}

// ── Audio tracks ──────────────────────────────────────────

export function addAudioTrack(state: ProjectState, track: AudioTrack) {
  state.workflow.audioTracks.push(track)
}

export function removeAudioTrack(state: ProjectState, trackId: string) {
  state.workflow.audioTracks = state.workflow.audioTracks.filter(
    (t) => t.id !== trackId,
  )
}

export function updateAudioTrack(
  state: ProjectState,
  trackId: string,
  updates: Partial<Omit<AudioTrack, 'id'>>,
) {
  const idx = state.workflow.audioTracks.findIndex((t) => t.id === trackId)
  if (idx === -1) return
  Object.assign(state.workflow.audioTracks[idx], updates)
}

// ── Text overlays ─────────────────────────────────────────

export function addTextOverlay(state: ProjectState, overlay: TextOverlay) {
  state.workflow.textOverlays.push(overlay)
}

export function updateTextOverlay(
  state: ProjectState,
  overlayId: string,
  updates: Partial<Omit<TextOverlay, 'id'>>,
) {
  const idx = state.workflow.textOverlays.findIndex((o) => o.id === overlayId)
  if (idx === -1) return
  Object.assign(state.workflow.textOverlays[idx], updates)
}

export function removeTextOverlay(state: ProjectState, overlayId: string) {
  state.workflow.textOverlays = state.workflow.textOverlays.filter(
    (o) => o.id !== overlayId,
  )
}

export function setTextOverlays(
  state: ProjectState,
  overlays: TextOverlay[],
) {
  state.workflow.textOverlays = overlays
}

// ── Split clip ────────────────────────────────────────────

export function splitClip(
  state: ProjectState,
  clipId: string,
  splitAtSeconds: number,
  newClipId: string,
) {
  const clip = state.clips[clipId]
  if (!clip) return
  // Guard: split must be within playable range
  const effectiveDur = (clip.duration - (clip.trimStart || 0) - (clip.trimEnd || 0)) / (clip.speed || 1)
  if (splitAtSeconds <= 0 || splitAtSeconds >= effectiveDur) return
  // Guard: clip must be in clipOrder (not soft-deleted)
  if (state.clipOrder.indexOf(clipId) === -1) return

  const originalTrimEnd = clip.trimEnd || 0
  const newTrimEnd = clip.duration - (clip.trimStart || 0) - splitAtSeconds * (clip.speed || 1)

  // Modify original: trim to split point
  clip.trimEnd = Math.max(0, newTrimEnd)
  clip.updatedAt = now()

  // Create second half
  const newClip: ClipState = {
    ...JSON.parse(JSON.stringify(clip)),
    _id: newClipId,
    orderIndex: clip.orderIndex + 1,
    trimStart: clip.duration - newTrimEnd - originalTrimEnd,
    trimEnd: originalTrimEnd,
    title: clip.title ? `${clip.title} (cont.)` : undefined,
    createdAt: now(),
    updatedAt: now(),
    deletedAt: undefined,
  }

  // Shift downstream clips
  state.clipOrder.forEach((id) => {
    const c = state.clips[id]
    if (c && c.orderIndex > clip.orderIndex) c.orderIndex++
  })

  state.clips[newClipId] = newClip
  state.clipOrder.splice(
    state.clipOrder.indexOf(clipId) + 1,
    0,
    newClipId,
  )
}
